import { useEffect, useState } from 'react';
import { apiFetch, v1Path } from '../../lib/api';
import {
  DEFAULT_WORK_ITEM_SUBTYPES,
  type LabelDto,
  type MemberDto,
  type ProjectDto,
  type WorkItemSubtypeDefinition,
} from '../../lib/work-types';
import { useOverlays } from './overlay-context';

function isLabelList(value: unknown): value is { items: LabelDto[] } {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as { items?: unknown }).items)
  );
}

function isMemberList(value: unknown): value is { items: MemberDto[] } {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as { items?: unknown }).items)
  );
}

export function useProjectDecorations(projectId: string | undefined) {
  const { toast } = useOverlays();
  const [labels, setLabels] = useState<LabelDto[]>([]);
  const [members, setMembers] = useState<MemberDto[]>([]);
  const [subtypes, setSubtypes] = useState<WorkItemSubtypeDefinition[]>(DEFAULT_WORK_ITEM_SUBTYPES);

  useEffect(() => {
    let cancelled = false;
    if (projectId) {
      void Promise.all([
        apiFetch<unknown>(v1Path(`/projects/${projectId}/labels`)),
        apiFetch<unknown>(v1Path(`/projects/${projectId}/members`)),
        apiFetch<ProjectDto>(v1Path(`/projects/${projectId}`)),
      ])
        .then(([labelRes, memberRes, project]) => {
          if (cancelled) return;
          if (!isLabelList(labelRes) || !isMemberList(memberRes)) {
            throw new Error('Invalid labels or members payload');
          }
          setLabels(labelRes.items);
          setMembers(memberRes.items.filter((row) => row.is_active !== false));
          setSubtypes(project.subtypes ?? DEFAULT_WORK_ITEM_SUBTYPES);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          toast(err instanceof Error ? err.message : 'Failed to load labels and members');
        });
      return () => {
        cancelled = true;
      };
    }

    setLabels([]);
    setSubtypes(DEFAULT_WORK_ITEM_SUBTYPES);
    void apiFetch<unknown>(v1Path('/members?limit=100&offset=0'))
      .then((memberRes) => {
        if (cancelled) return;
        if (!isMemberList(memberRes)) {
          throw new Error('Invalid members payload');
        }
        setMembers(memberRes.items.filter((row) => row.is_active !== false));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        toast(err instanceof Error ? err.message : 'Failed to load members');
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, toast]);

  return { labels, members, subtypes };
}
